if (!customElements.get('personal-notebook-gallery')) {
  customElements.define(
    'personal-notebook-gallery',
    class PersonalNotebookGallery extends HTMLElement {
      connectedCallback() {
        this.viewer = this.querySelector('[id^="GalleryViewer-"]');
        this.track = this.querySelector('[id^="Slider-Gallery-"]');
        this.slides = [...this.querySelectorAll('[data-gallery-index]')];
        this.dots = [...this.querySelectorAll('[data-gallery-dot]')];
        this.frontLabel = this.dataset.frontLabel || 'כריכה ראשית';
        this.backLabel = this.dataset.backLabel || 'כריכה אחורית';
        this.currentIndex = 0;
        this.syncing = false;

        this.viewer.addEventListener('slideChanged', ({ detail }) => this.activateSlide(detail.currentElement));
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goToIndex(Number(dot.dataset.galleryDot)));
        });

        this.observer = new MutationObserver(() => {
          this.connectZepto();
          this.connectLogoPositionEditor();
          this.connectCoverCropEditor();
        });
        this.observer.observe(document.body, { childList: true, subtree: true });
        this.connectZepto();
        this.connectLogoPositionEditor();
        this.connectCoverCropEditor();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.floatingPreviewObserver?.disconnect();
        this.logoPositionPreviewObserver?.disconnect();
        this.unmountLogoPositionStage();
        this.closeCoverCropEditor(false);
        this.unbindTextPositionPreview();
      }

      connectZepto() {
        if (this.textPositionModalOpen) {
          this.repairTextPositionModalPreview();
          return;
        }
        if (this.logoPositionState) {
          this.repairLogoPositionStagePreview();
          return;
        }

        this.personalizer = document.querySelector('.product-personalizer');
        if (!this.personalizer) return;

        this.makeCoverUploadsOptional();
        this.arrangePersonalizer();
        this.namespaceCoverProperties();
        this.syncCoverUploadActions();
        const host = this.querySelector('[data-zepto-editor-host]');
        const preview = this.querySelector('.pplr_preview_wrapper');
        if (!host || !preview?.querySelector('#pplr_canvas')) return;

        this.bindViewControl('front', this.frontLabel);
        this.bindViewControl('back', this.backLabel);
        this.host = host;
        this.preview = preview;
        this.clearInitialTextSlots();
        if (this.zeptoReady) {
          if (this.textPositionModalOpen) return;

          const activeSlide = this.slides[this.currentIndex] || this.slides[0];
          const view = activeSlide.dataset.editorView;
          const stage = this.querySelector(`[data-editor-view="${view}"] [data-editor-stage]`);
          const control = this.findViewControl(view);
          const controlActive =
            !control ||
            control.classList.contains('pplr_active') ||
            control.getAttribute('aria-selected') === 'true';

          if (
            view &&
            (this.host.parentElement !== stage ||
              this.preview.parentElement !== this.host ||
              !controlActive)
          ) {
            this.activateEditor(view);
          }
          this.syncFloatingPreview(view);
          this.finishPlaygroundLoading();
          return;
        }

        this.zeptoReady = true;
        this.classList.add('is-zepto-ready');
        const activeSlide = this.slides[this.currentIndex] || this.slides[0];
        this.activateEditor(activeSlide.dataset.editorView || 'front');
        this.finishPlaygroundLoading();
      }

      finishPlaygroundLoading() {
        const product = this.closest('.personal-notebook-product');
        if (!product) return;

        product.classList.remove('is-playground-loading');
        product.classList.add('is-playground-ready');
        product.setAttribute('aria-busy', 'false');
      }

      repairTextPositionModalPreview() {
        if (!this.textPositionModalOpen || !this.host || !this.preview || !this.textPositionModalStage) {
          return false;
        }

        if (this.host.parentElement !== this.textPositionModalStage) {
          this.textPositionModalStage.append(this.host);
        }
        if (this.preview.parentElement !== this.host) {
          this.host.append(this.preview);
        }

        const frame = String(this.textPositionControl?.frame || '');
        const originalStage = this.textPositionControl?.originalStage;
        const originalStagePreviews = originalStage?.querySelectorAll
          ? [...originalStage.querySelectorAll('.pplr_preview_wrapper')]
          : [originalStage?.querySelector?.('.pplr_preview_wrapper')].filter(Boolean);
        const galleryPreviews = this.querySelectorAll
          ? [...this.querySelectorAll('.pplr_preview_wrapper')]
          : [];
        const renderedPreview = [...new Set([
          ...this.host.querySelectorAll('.pplr_preview_wrapper'),
          ...originalStagePreviews,
          ...galleryPreviews,
        ])].find(
          (candidate) =>
            candidate !== this.preview &&
            String(candidate.querySelector('#pplr_canvas')?.dataset.frame || '') === frame
        );
        const renderedCanvas = renderedPreview?.querySelector('#pplr_canvas');
        const modalCanvas = this.preview.querySelector('#pplr_canvas');
        if (!renderedCanvas || renderedCanvas === modalCanvas) {
          return String(modalCanvas?.dataset.frame || '') === frame;
        }

        if (modalCanvas) modalCanvas.replaceWith(renderedCanvas);
        else this.preview.append(renderedCanvas);
        renderedPreview.remove();
        return String(this.preview.querySelector('#pplr_canvas')?.dataset.frame || '') === frame;
      }

      makeCoverUploadsOptional() {
        const coverUploads = [
          'properties[כריכה ראשית – העלאת עיצוב מלא]',
          'properties[כריכה אחורית – העלאת עיצוב מלא]',
        ];

        this.personalizer.querySelectorAll('input[type="file"]').forEach((field) => {
          if (!coverUploads.includes(field.name)) return;

          field.required = false;
          field.removeAttribute('required');
          field.classList.remove('cstmfy_c_required');
        });
      }

      syncFloatingPreview(view) {
        const container = document.querySelector('.pplrabs .pplr-prev-left');
        const source = this.querySelector(`[data-editor-view="${view}"] [data-notebook-binding]`);
        const base = container?.querySelector(':scope > img');
        if (!container || !source || !base) return;

        if (this.floatingBase !== base) {
          this.floatingPreviewObserver?.disconnect();
          this.floatingBase = base;
          this.floatingPreviewObserver = new MutationObserver(() => {
            const activeSlide = this.slides[this.currentIndex] || this.slides[0];
            this.syncFloatingPreview(this.activeEditorView || activeSlide.dataset.editorView);
          });
          this.floatingPreviewObserver.observe(base, { attributes: true, attributeFilter: ['src'] });
        }

        let stage = container.querySelector('[data-floating-notebook-stage]');
        if (!stage) {
          stage = document.createElement('span');
          stage.className = 'personal-notebook-floating-stage';
          stage.dataset.floatingNotebookStage = '';
          const binding = source.cloneNode(false);
          stage.append(binding);
          container.append(stage);
        }

        stage.dataset.editorView = view;
        const binding = stage.querySelector('[data-notebook-binding]');
        const bindingSource = source.currentSrc || source.src;
        if (binding.src !== bindingSource) binding.src = bindingSource;
        this.syncGalleryComposite(view, base);
      }

      syncGalleryComposite(view, base) {
        const renderedSource = base.src || base.currentSrc;
        const stage = this.querySelector(`[data-editor-view="${view}"] [data-editor-stage]`);
        if (!view || !renderedSource || !stage) return;

        this.galleryCompositeSources ||= {};
        const viewChanged =
          this.lastGalleryCompositeView && this.lastGalleryCompositeView !== view;
        const sourceChanged = this.lastGalleryCompositeSource !== renderedSource;
        if (!viewChanged || sourceChanged || !this.lastGalleryCompositeView) {
          this.galleryCompositeSources[view] = renderedSource;
        }
        this.lastGalleryCompositeView = view;
        this.lastGalleryCompositeSource = renderedSource;
        const viewSource = this.galleryCompositeSources[view];
        if (!viewSource) return;

        let composite = stage.querySelector('[data-notebook-composite]');
        if (!composite) {
          composite = document.createElement('img');
          composite.className = 'personal-notebook-composite';
          composite.dataset.notebookComposite = '';
          composite.alt = '';
          composite.setAttribute('aria-hidden', 'true');
          stage.append(composite);
        }

        if (composite.src !== viewSource) composite.src = viewSource;
      }

      bindViewControl(view, label) {
        const control = this.findViewControl(view);
        if (!control || control.dataset.personalNotebookBound) return;

        if (control.textContent.trim() !== label) control.textContent = label;
        control.dataset.personalNotebookBound = 'true';
        control.addEventListener('click', () => {
          if (this.syncing) return;

          this.activeEditorView = view;
          this.goToIndex(view === 'front' ? 0 : 1);
          requestAnimationFrame(() => this.activateEditor(view));
        });
      }

      findViewControl(view) {
        if (!this.personalizer) return null;

        const labels = view === 'front' ? [this.frontLabel, 'Main View'] : [this.backLabel];
        return [...this.personalizer.querySelectorAll('.pplr_tab_index, button, [role="tab"]')].find(
          (element) => labels.includes(element.textContent.trim())
        );
      }

      findOptionGroup(propertyName) {
        const field = [...this.personalizer.querySelectorAll('[name]')].find(
          (element) => element.name === `properties[${propertyName}]`
        );

        return field?.closest('.pplr-wrapper') || null;
      }

      setupInternalPagesGallery(group) {
        if (group.dataset.personalNotebookInternalPages) return;

        const input = group.querySelector?.('input[name="properties[סוג דפי פנים]"]');
        const track = group.querySelector?.('.pplr_thumb_image');
        const pages = [...(track?.querySelectorAll?.('.pplr-swatch-element.pplrimage') || [])];
        if (!input || !track || !pages.length) return;

        group.dataset.personalNotebookInternalPages = 'true';
        group.classList.add('personal-notebook-internal-pages');
        track.classList.add('personal-notebook-internal-pages-track');

        const frame = document.createElement('div');
        frame.classList.add('personal-notebook-internal-pages-gallery');
        frame.setAttribute('role', 'group');
        frame.setAttribute('aria-label', 'בחירת סוג דפי פנים');
        frame.setAttribute('tabindex', '0');
        track.parentElement.insertBefore(frame, track);
        frame.append(track);
        const internalPagesLabel =
          group.querySelector?.('.oindexlabel') || group.querySelector?.('.pplrlabel');
        const internalPagesSection = this.createCoverSection('סוג דפי פנים');
        internalPagesLabel?.classList.add('personal-notebook-option-label-hidden');
        frame.parentElement.insertBefore(internalPagesSection, frame);

        const previousButton = document.createElement('button');
        previousButton.type = 'button';
        previousButton.classList.add(
          'personal-notebook-internal-pages-button',
          'personal-notebook-internal-pages-button--previous'
        );
        previousButton.setAttribute('aria-label', 'סוג הדפים הקודם');
        previousButton.textContent = '‹';
        frame.insertBefore(previousButton, track);

        const nextButton = document.createElement('button');
        nextButton.type = 'button';
        nextButton.classList.add(
          'personal-notebook-internal-pages-button',
          'personal-notebook-internal-pages-button--next'
        );
        nextButton.setAttribute('aria-label', 'סוג הדפים הבא');
        nextButton.textContent = '›';
        frame.append(nextButton);

        const caption = document.createElement('p');
        caption.classList.add('personal-notebook-internal-pages-caption');
        caption.setAttribute('aria-live', 'polite');
        group.append(caption);

        const dialog = document.createElement('dialog');
        dialog.classList.add('personal-notebook-internal-pages-dialog');
        const closeButton = document.createElement('button');
        closeButton.type = 'button';
        closeButton.classList.add('personal-notebook-internal-pages-dialog-close');
        closeButton.setAttribute('aria-label', 'סגירת התצוגה המקדימה');
        closeButton.textContent = '×';
        const previewImage = document.createElement('img');
        previewImage.classList.add('personal-notebook-internal-pages-preview-image');
        const previewCaption = document.createElement('p');
        previewCaption.classList.add('personal-notebook-internal-pages-preview-caption');
        dialog.append(closeButton, previewImage, previewCaption);
        group.append(dialog);

        const selectedIndex = pages.findIndex((page) => page.dataset.val === input.value);
        const internalPagesPreviewInput = document.createElement('input');
        internalPagesPreviewInput.type = 'hidden';
        internalPagesPreviewInput.name = 'properties[_תצוגת דפים פנימיים]';
        internalPagesPreviewInput.setAttribute('form', input.getAttribute('form'));
        group.append(internalPagesPreviewInput);
        this.internalPagesInput = input;
        this.internalPagesPreviewInput = internalPagesPreviewInput;
        this.internalPages = pages;
        this.internalPagesIndex = selectedIndex < 0 ? 0 : selectedIndex;
        this.internalPagePreloads = pages.map((page) => {
          const image = document.createElement('img');
          image.fetchPriority = 'low';
          image.decoding = 'async';
          image.src = page.dataset.image;
          return image;
        });
        this.internalPagesFrame = frame;
        this.internalPagesCaption = caption;
        this.internalPagesPreviousButton = previousButton;
        this.internalPagesNextButton = nextButton;
        this.internalPagesDialog = dialog;
        this.internalPagesPreviewClose = closeButton;
        this.internalPagesPreviewImage = previewImage;
        this.internalPagesPreviewCaption = previewCaption;

        previousButton.addEventListener('click', () => this.showInternalPage(this.internalPagesIndex - 1));
        nextButton.addEventListener('click', () => this.showInternalPage(this.internalPagesIndex + 1));
        frame.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.openInternalPagePreview(this.internalPages[this.internalPagesIndex]);
            return;
          }
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;

          event.preventDefault();
          this.showInternalPage(this.internalPagesIndex + (event.key === 'ArrowLeft' ? 1 : -1));
        });
        frame.addEventListener(
          'touchstart',
          (event) => {
            this.internalPagesTouchStart = event.touches[0].clientX;
          },
          { passive: true }
        );
        frame.addEventListener(
          'touchend',
          (event) => {
            const distance = this.internalPagesTouchStart - event.changedTouches[0].clientX;
            if (Math.abs(distance) < 40) return;

            this.showInternalPage(this.internalPagesIndex + (distance > 0 ? 1 : -1));
          },
          { passive: true }
        );
        pages.forEach((page) => {
          page.addEventListener('click', (event) => {
            if (event.isTrusted) this.openInternalPagePreview(page);
          });
        });
        closeButton.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });

        this.showInternalPage(this.internalPagesIndex, false);
      }

      showInternalPage(index, select = true) {
        const pageCount = this.internalPages.length;
        const nextIndex = (index + pageCount) % pageCount;
        const activePage = this.internalPages[nextIndex];

        this.internalPagesIndex = nextIndex;
        this.internalPages.forEach((page, pageIndex) => {
          const active = pageIndex === nextIndex;
          page.classList.toggle('is-gallery-active', active);
          page.setAttribute('aria-hidden', String(!active));
        });
        this.internalPagesCaption.textContent = activePage.dataset.val;
        this.internalPagesPreviewInput.value = activePage.dataset.image;
        if (select && this.internalPagesInput.value !== activePage.dataset.val) activePage.click();
      }

      openInternalPagePreview(page) {
        this.internalPagesPreviewImage.src = page.dataset.image;
        this.internalPagesPreviewImage.alt = page.dataset.val;
        this.internalPagesPreviewCaption.textContent = page.dataset.val;
        this.internalPagesDialog.showModal();
      }

      arrangePersonalizer() {
        const tabWrapper = this.personalizer.querySelector('.pplr_tab_wrapper');
        if (!tabWrapper) return;

        this.personalizer.classList.add('personal-notebook-personalizer');
        ['בחרי גודל מחברתיומן', 'סוג דפי פנים'].forEach((propertyName) => {
          const group = this.findOptionGroup(propertyName);
          if (!group) return;

          if (!group.dataset.personalNotebookShared) {
            group.dataset.personalNotebookShared = 'true';
            group.classList.add('personal-notebook-shared-option');
            this.personalizer.insertBefore(group, tabWrapper);
          }
          if (propertyName === 'בחרי גודל מחברתיומן') {
            this.setupNotebookSizeTooltip(group);
          }
          if (propertyName === 'סוג דפי פנים') this.setupInternalPagesGallery(group);
        });
      }

      setupNotebookSizeTooltip(group) {
        group.dataset ||= {};
        if (group.dataset.personalNotebookSizeTooltip) return;

        const label = group.querySelector?.('.pplrlabel') || group.querySelector?.('label');
        const heading = group.querySelector?.('.oindexlabel');
        const tooltip = this.ensureCoverTooltip(label);
        if (!heading || !tooltip) return;

        group.dataset.personalNotebookSizeTooltip = 'true';
        const section = this.createCoverSection(heading.textContent.trim(), tooltip);
        heading.parentElement.insertBefore(section, heading);
        heading.classList.add('personal-notebook-option-label-hidden');
        label.classList.add('personal-notebook-option-label-hidden');
        this.setCoverTooltipContent(tooltip, [
          {
            text: 'הגודל הסטנדרטי (17.5 × 24.5 ס״מ) נוח לנשיאה בתיק.',
          },
          {
            text: 'גודל A4 ‏(21 × 29.7 ס״מ) מתאים למי שזקוקה למרחב כתיבה גדול יותר.',
          },
          {
            text: 'המחיר מתעדכן אוטומטית בבחירת A4.',
          },
        ]);
      }

      namespaceCoverProperties() {
        const propertyLabels = new Map([
          ['properties[בחרי פונט לכיתוב]', 'פונט'],
          ['properties[Select a Color for Single Line Text]', 'צבע טקסט'],
        ]);

        [...this.personalizer.querySelectorAll('.pplr_tab')].slice(0, 2).forEach((panel, index) => {
          const coverLabel = index === 0 ? this.frontLabel : this.backLabel;
          const view = index === 0 ? 'front' : 'back';
          panel.querySelectorAll('[name]').forEach((field) => {
            const propertyLabel = propertyLabels.get(field.name);
            if (field.name === 'properties[Select a Color for Single Line Text]') {
              const label = field.closest?.('.pplr-wrapper')?.querySelector('label');
              if (label) label.textContent = 'בחירת צבע פונט';
            }
            if (propertyLabel) field.name = `properties[${coverLabel} – ${propertyLabel}]`;
          });
          this.textSlotsByView ||= {};
          if (!this.textSlotsByView[view]) {
            this.textSlotsByView[view] = this.discoverTextSlots(panel, coverLabel, view);
            if (!this.textSlotsByView[view].length) return;
            this.setupTextSlotList(panel, coverLabel, view, this.textSlotsByView[view]);
          }
          this.organizeCoverPanel(panel, coverLabel, view);
        });
      }

      organizeCoverPanel(panel, coverLabel, view) {
        panel.dataset ||= {};
        if (panel.dataset.personalNotebookOrganized) return;

        const fields = [...panel.querySelectorAll('[name]')];
        const findGroup = (label) =>
          fields.find((field) => field.name?.includes(`${coverLabel} – ${label}`))?.closest?.(
            '.pplr-wrapper'
          );
        const backgroundGroup = findGroup('בחירת רקע');
        const fullUploadGroup = findGroup('העלאת עיצוב מלא');
        const logoUploadGroup = findGroup('העלאת לוגו');
        const textList = this.textSlotLists?.[view];
        if (!backgroundGroup || !fullUploadGroup || !logoUploadGroup || !textList) return;

        panel.dataset.personalNotebookOrganized = 'true';
        backgroundGroup.classList.add('personal-notebook-cover-background');
        fullUploadGroup.classList.add('personal-notebook-cover-upload');
        logoUploadGroup.classList.add('personal-notebook-logo-upload');
        backgroundGroup
          .querySelector('.pplrlabel')
          ?.classList.add('personal-notebook-option-label-hidden');
        fullUploadGroup
          .querySelector('.oindexlabel')
          ?.classList.add('personal-notebook-option-label-hidden');

        const fullUploadLabel = fullUploadGroup.querySelector('.pplrlabel');
        this.decorateCoverUpload(
          fullUploadGroup,
          'לחצי להעלאת תמונת עיצוב לכריכה'
        );
        const fullUploadTooltip = this.ensureCoverTooltip(fullUploadLabel);
        this.setCoverTooltipContent(fullUploadTooltip, [
          { text: 'הנחיות להעלאת עיצוב:', strong: true },
          {
            text: 'גודל סטנדרט (17.5 × 24.5 ס״מ): מומלץ להעלות קובץ ברזולוציה גבוהה (300dpi) במידות אלו.',
          },
          {
            text: 'גודל A4: במידה ובחרת בתוספת גודל A4, יש להעלות עיצוב במידות 21 × 29.7 ס״מ.',
          },
          {
            text: 'טיפ חשוב: וודאו שכל הטקסטים והאלמנטים החשובים נמצאים לפחות 1 ס״מ מהקצוות כדי שלא ייחתכו בייצור.',
          },
          { text: 'פורמטים נתמכים: JPG, PNG.' },
          {
            before: 'את יודעת מה את רוצה אבל אין לך תמונה מעוצבת מתאימה? ',
            link: 'צרי קשר',
            after: ' לקבלת שירותי עיצוב אישיים!',
            breakBeforeLink: true,
          },
        ]);
        fullUploadLabel?.classList.add('personal-notebook-option-label-hidden');

        const logoLabel = logoUploadGroup.querySelector('.pplrlabel');
        this.decorateCoverUpload(logoUploadGroup, 'לחצי להעלאת תמונת לוגו');
        const logoTooltip = this.ensureCoverTooltip(logoLabel);
        this.setCoverTooltipContent(logoTooltip, [
          {
            text: 'כאן ניתן להעלות לוגו או אלמנט גרפי שיופיע במרכז הכריכה (ניתן להזיז).',
          },
          {
            text: 'לקבלת התוצאה הטובה ביותר, מומלץ להעלות קובץ PNG עם רקע שקוף.',
          },
          { text: 'וודאי שהלוגו איכותי וברור.' },
          {
            before: 'אין לך לוגו אבל את רוצה לעצב לך לוגו משלך? ',
            link: 'צרי קשר',
            after: ' לקבלת שירותי עיצוב לוגו!',
            breakBeforeLink: true,
          },
        ]);
        logoLabel?.classList.add('personal-notebook-option-label-hidden');

        const textTooltipHost = document.createElement('span');
        const textTooltip = this.ensureCoverTooltip(textTooltipHost);
        this.setCoverTooltipContent(textTooltip, [
          { text: 'ניתן להוסיף עד 4 כיתובים, עם התאמה אישית של:' },
          { items: ['גודל.', 'צבע.', 'פונט.', 'מיקום.'] },
          {
            before: 'לא בטוחה מה לכתוב?',
            link: 'צרי קשר',
            after: ' ואשמח לעזור בתכנון!',
            breakBeforeLink: true,
          },
        ]);

        const backgroundTooltipHost = document.createElement('span');
        const backgroundTooltip = this.ensureCoverTooltip(backgroundTooltipHost);
        this.setCoverTooltipContent(backgroundTooltip, [
          { text: 'ניתן לבחור ברקע מערכת הרקעים המוצעת.' },
          { text: 'בנוסף ניתן להעלות תמונת רקע עם עיצוב משלך.' },
        ]);

        const backgroundSection = this.createCoverSection(
          'בחירת עיצוב רקע',
          backgroundTooltip
        );
        const fullUploadSection = this.createCoverSection(
          'העלאת עיצוב מלא לכריכה',
          fullUploadTooltip
        );
        const logoSection = this.createCoverSection('העלאת תמונת לוגו', logoTooltip);
        const textSection = this.createCoverSection(
          'הוספת כיתובים מותאמים אישית',
          textTooltip
        );
        panel.append(
          backgroundSection,
          backgroundGroup,
          fullUploadSection,
          fullUploadGroup,
          logoSection,
          logoUploadGroup,
          textSection,
          textList
        );
      }

      decorateCoverUpload(group, text) {
        if (group.dataset.personalNotebookUploadDecorated) return;

        const input = group.querySelector('input[type="file"]');
        const uploadContainer = group.querySelector('.pplrfileuploadbutton');
        if (!input || !uploadContainer) return;

        input.setAttribute('accept', 'image/jpeg,image/png');
        group.dataset.personalNotebookUploadDecorated = 'true';
        group.classList.add('personal-notebook-decorated-upload');
        const button = this.createPersonalNotebookActionButton(text, 'upload');
        button.className = 'personal-notebook-action-button personal-notebook-upload-action';
        button.addEventListener('click', () => input.click());
        group.insertBefore(button, uploadContainer);
        this.coverUploadActions ||= new Map();
        this.coverUploadActions.set(group, button);
        this.syncCoverUploadAction(group, button, input);
      }

      syncCoverUploadActions() {
        this.coverUploadActions?.forEach((button, group) => {
          this.syncCoverUploadAction(group, button, group.querySelector('input[type="file"]'));
        });
      }

      syncCoverUploadAction(group, button, input) {
        if (!input) return;

        if (!input.dataset.personalNotebookUploadActionBound) {
          input.dataset.personalNotebookUploadActionBound = 'true';
          input.addEventListener('change', () => this.syncCoverUploadAction(group, button, input));
        }
        button.hidden = Boolean(input.files?.length);
      }

      createPersonalNotebookActionButton(text, iconName) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'personal-notebook-action-button';
        button.textContent = text;
        button.setAttribute('aria-label', text);

        const icon = document.createElement('span');
        icon.className = `personal-notebook-action-icon personal-notebook-action-icon--${iconName}`;
        icon.setAttribute('aria-hidden', 'true');
        button.append(icon);
        return button;
      }

      createCoverSection(title, tooltip) {
        const heading = document.createElement('h3');
        heading.className = 'personal-notebook-cover-section';
        heading.textContent = title;
        if (tooltip) heading.append(tooltip);
        return heading;
      }

      setCoverOptionLabel(label, text) {
        if (!label) return;

        const textNode = [...label.childNodes].find(
          (node) => node.nodeType === 3 && node.textContent.trim()
        );
        if (textNode) textNode.textContent = `${text} `;
        else label.prepend(document.createTextNode(`${text} `));
      }

      ensureCoverTooltip(label) {
        if (!label) return null;

        let tooltip = label.querySelector('.ptooltip');
        if (tooltip) return tooltip;

        tooltip = document.createElement('span');
        tooltip.className = 'ptooltip';
        const icon = document.createElement('span');
        icon.className = 'qcon';
        icon.textContent = '?';
        const content = document.createElement('span');
        content.className = 'ptooltiptext';
        tooltip.append(icon, content);
        label.append(tooltip);
        return tooltip;
      }

      setCoverTooltipContent(tooltip, paragraphs) {
        const content = tooltip?.querySelector('.ptooltiptext');
        if (!content) return;

        tooltip.classList.add('personal-notebook-cover-tooltip');
        const icon = tooltip.querySelector('.qcon');
        icon?.setAttribute('tabindex', '0');
        icon?.setAttribute('role', 'button');
        icon?.setAttribute('aria-label', 'מידע נוסף');
        content.setAttribute('role', 'tooltip');
        content.replaceChildren();
        paragraphs.forEach(({ text, strong, before, link, after, items, breakBeforeLink }) => {
          if (items) {
            const list = document.createElement('ul');
            list.className = 'personal-notebook-cover-tooltip-list';
            items.forEach((itemText) => {
              const item = document.createElement('li');
              item.textContent = itemText;
              list.append(item);
            });
            content.append(list);
            return;
          }

          const paragraph = document.createElement('span');
          paragraph.className = 'personal-notebook-cover-tooltip-paragraph';
          if (strong) {
            const emphasis = document.createElement('strong');
            emphasis.textContent = text;
            paragraph.append(emphasis);
          } else if (link) {
            paragraph.append(document.createTextNode(before));
            if (breakBeforeLink) paragraph.append(document.createElement('br'));
            const anchor = document.createElement('a');
            anchor.href = '/pages/contact';
            anchor.textContent = link;
            paragraph.append(anchor, document.createTextNode(after));
          } else {
            paragraph.textContent = text;
          }
          content.append(paragraph);
        });
        const arrow = document.createElement('span');
        arrow.className = 'ptooltiparrow';
        content.append(arrow);
      }

      clearInitialTextSlots() {
        if (this.initialTextSlotsCleared || !this.host || !this.preview) return;

        Object.values(this.textSlotsByView || {})
          .flat()
          .forEach((slot) => {
            slot.defaultState = this.captureTextSlotState(slot);
            slot.defaultState.text = '';
            globalThis.window?.LoadPplrWithFont?.(slot.frame, true, slot.text);
          });
        this.initialTextSlotsCleared = true;
      }

      discoverTextSlots(panel, coverLabel, view) {
        const fields = [...panel.querySelectorAll('[name]')];
        const texts = fields
          .filter((field) => field.classList?.contains?.('pplr_text'))
          .slice(0, 4);

        return texts.map((text, slotIndex) => {
          const index = slotIndex + 1;
          const frame = text.dataset.frame;
          const frameFields = fields.filter((field) => field.dataset?.frame === frame);
          frameFields.forEach((field) => {
            field.disabled = false;
          });
          const font = frameFields.find((field) => field.classList?.contains?.('pplr_font'));
          const color = frameFields.find((field) => field.classList?.contains?.('pplrjscolor'));

          text.name = `properties[${coverLabel} – כיתוב ${index}]`;
          text.placeholder = 'עריכת כיתוב';
          text.setAttribute?.('d-placeholder', 'עריכת כיתוב');
          text.classList.remove('pplr_hide_duplicate');
          text.value = '';
          if (font) font.name = `properties[${coverLabel} – כיתוב ${index} – פונט]`;
          if (color) color.name = `properties[${coverLabel} – כיתוב ${index} – צבע]`;

          const slot = {
            index,
            view,
            frame,
            text,
            font,
            color,
            textGroup: text.closest?.('.pplr-wrapper'),
            fontGroup: font?.closest?.('.pplr-wrapper'),
            colorGroup: color?.closest?.('.pplr-wrapper'),
          };
          [slot.textGroup, slot.fontGroup, slot.colorGroup].forEach((group) => {
            group?.classList?.remove('p_h_d_p', 'pplr_hide_duplicate');
            group?.style?.removeProperty('display');
          });
          slot.sizeGroup = this.setupFontSizeControl(panel, coverLabel, slot);
          slot.size = slot.sizeGroup?.querySelector?.('select');
          return slot;
        });
      }

      setupTextSlotList(panel, coverLabel, view, slots) {
        this.textSlotLists ||= {};
        if (this.textSlotLists[view]) return;

        slots.forEach((slot) => {
          [slot.textGroup, slot.sizeGroup, slot.fontGroup, slot.colorGroup].forEach((group) => {
            group?.classList?.add('personal-notebook-modal-only-control');
          });
        });

        const list = document.createElement('div');
        list.className = 'personal-notebook-text-slot-list';
        list.style.clear = 'both';
        list.style.width = '100%';
        list.dataset.editorView = view;
        list.setAttribute('aria-label', `${coverLabel} – כיתובים`);
        panel.append(list);
        this.textSlotLists[view] = list;
        this.renderTextSlotList(view);
      }

      renderTextSlotList(view) {
        const list = this.textSlotLists?.[view];
        const slots = this.textSlotsByView?.[view] || [];
        if (!list) return;

        list.children.slice?.().forEach((child) => child.remove());
        while (list.children.length) list.children[0].remove();

        slots
          .filter((slot) => slot.text.value.trim())
          .forEach((slot) => {
            const row = document.createElement('div');
            row.className = 'personal-notebook-text-slot-row';

            const preview = document.createElement('span');
            preview.className = 'personal-notebook-text-slot-preview';
            preview.textContent = slot.text.value;
            preview.style.fontFamily = this.getTextSlotFontFamily(slot);
            preview.style.color =
              slot.color?.dataset?.value || slot.color?.getAttribute?.('data-value') || '';

            const editButton = document.createElement('button');
            editButton.type = 'button';
            editButton.className = 'personal-notebook-text-slot-action';
            editButton.textContent = '✎';
            editButton.setAttribute('aria-label', `עריכת כיתוב ${slot.index}`);
            editButton.addEventListener('click', () => this.openTextSlot(slot, 'edit'));

            const deleteButton = document.createElement('button');
            deleteButton.type = 'button';
            deleteButton.className = 'personal-notebook-text-slot-action';
            deleteButton.textContent = '×';
            deleteButton.setAttribute('aria-label', `מחיקת כיתוב ${slot.index}`);
            deleteButton.addEventListener('click', () => this.deleteTextSlot(slot));

            row.append(preview, editButton, deleteButton);
            list.append(row);
          });

        const emptySlot = slots.find((slot) => !slot.text.value.trim());
        if (!emptySlot) return;

        const addButton = this.createPersonalNotebookActionButton('לחצי להוספת כיתוב', 'text');
        addButton.className = 'personal-notebook-text-slot-add personal-notebook-action-button';
        addButton.addEventListener('click', () => this.openTextSlot(emptySlot, 'add'));
        list.append(addButton);
      }

      getTextSlotFontFamily(slot) {
        const fontFile = slot.font?.dataset?.value || slot.font?.getAttribute?.('data-value') || '';
        return String(fontFile).split('/').pop().replace(/\.[^/.]+$/, '') || slot.font?.value || '';
      }

      openTextSlot(slot, mode) {
        this.openTextPositionModal({
          ...slot,
          slot,
          mode,
          button: null,
        });
      }

      deleteTextSlot(slot) {
        if (slot.defaultState) this.restoreTextSlotState(slot, slot.defaultState);
        else {
          slot.text.value = '';
          globalThis.window?.LoadPplrWithFont?.(slot.frame, true, slot.text);
        }
        this.renderTextSlotList(slot.view);
      }

      setupFontSizeControl(panel, coverLabel, slot) {
        const fields = [...panel.querySelectorAll('[name]')];
        const font =
          slot?.font ||
          fields.find((field) => field.name === `properties[${coverLabel} – פונט]`);
        const text =
          slot?.text ||
          fields.find((field) => field.name === `properties[${coverLabel} – שם או הקדשה]`);
        if (!font || !text) return;
        const existing = panel.querySelector(
          `[data-personal-notebook-font-size="${text.dataset.frame}"]`
        );
        if (existing) return existing;

        const fontGroup = font?.closest('.pplr-wrapper');
        if (!fontGroup) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'pplr-wrapper personal-notebook-font-size';
        wrapper.dataset.personalNotebookFontSize = text.dataset.frame;

        const label = document.createElement('label');
        label.className = 'pplrlabel';
        label.textContent = 'גודל פונט';

        const select = document.createElement('select');
        select.className = 'pplr_select personal-notebook-font-size-select';
        select.name = slot
          ? `properties[${coverLabel} – כיתוב ${slot.index} – גודל]`
          : `properties[${coverLabel} – גודל פונט]`;
        select.dataset.frame = text.dataset.frame;
        select.setAttribute('form', text.getAttribute('form'));
        const frameValues = window.pplr_values?.[select.dataset.frame];
        if (frameValues) {
          frameValues[4] = 2 * Math.min(frameValues[2], 100 - frameValues[2]);
          frameValues[14] = 2 * Math.min(frameValues[3], 100 - frameValues[3]);
        }
        for (let size = 16; size <= 120; size += 2) {
          const option = document.createElement('option');
          option.value = String(size);
          option.textContent = `${size}px`;
          select.append(option);
        }
        select.value = String(window.pplr_values?.[select.dataset.frame]?.[1] ?? 20);
        select.addEventListener('change', () => {
          const frameValues = window.pplr_values?.[select.dataset.frame];
          if (!frameValues) return;

          frameValues[1] = Number(select.value);
          window.LoadPplrWithFont?.(select.dataset.frame, true, select);
        });

        wrapper.append(label, select);
        fontGroup.after(wrapper);
        return wrapper;
      }

      setupTextPositionControl(panel, coverLabel) {
        if (panel.querySelector('[data-personal-notebook-text-position]')) return;

        const fields = [...panel.querySelectorAll('[name]')];
        const text = fields.find(
          (field) => field.name === `properties[${coverLabel} – שם או הקדשה]`
        );
        const font = fields.find((field) => field.name === `properties[${coverLabel} – פונט]`);
        const color = fields.find((field) => field.name === `properties[${coverLabel} – צבע טקסט]`);
        const textGroup = text?.closest?.('.pplr-wrapper');
        const sizeGroup = panel.querySelector('[data-personal-notebook-font-size]');
        const fontGroup = font?.closest?.('.pplr-wrapper');
        const colorGroup = color?.closest?.('.pplr-wrapper');
        const anchor = sizeGroup || textGroup;
        if (!text || !anchor) return;

        text.placeholder = 'עריכת כיתוב';
        text.setAttribute?.('d-placeholder', 'עריכת כיתוב');
        if (text.value === 'הקלידו כאן') {
          text.value = 'עריכת כיתוב';
          globalThis.window?.LoadPplrWithFont?.(text.dataset.frame, true, text);
        }
        [textGroup, sizeGroup, fontGroup, colorGroup].forEach((group) => {
          group?.classList?.add('personal-notebook-modal-only-control');
        });
        const wrapper = document.createElement('div');
        wrapper.className = 'pplr-wrapper personal-notebook-text-position';
        wrapper.dataset.personalNotebookTextPosition = '';

        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'personal-notebook-text-position-button';
        button.textContent = 'עדכני מיקום הטקסט';
        button.setAttribute('aria-haspopup', 'dialog');

        button.addEventListener('click', () => {
          this.openTextPositionModal({
            button,
            text,
            frame: text.dataset.frame,
            view: coverLabel === this.frontLabel ? 'front' : 'back',
            textGroup,
            sizeGroup,
            fontGroup,
            colorGroup,
          });
        });

        wrapper.append(button);
        anchor.after(wrapper);
      }

      openLogoPositionEditor(editor, input) {
        const frame = input?.dataset.frame;
        const values = window.pplr_values?.[frame];
        if (!editor || !frame || !values) return;
        if (this.logoPositionState?.frame === frame) {
          if (this.logoPositionState.editor === editor) return;
          const currentEditorVisible = Boolean(
            this.logoPositionState.editor.getClientRects?.().length ||
              this.logoPositionState.editor.offsetParent
          );
          if (currentEditorVisible) return;

          this.unmountLogoPositionStage();
          this.logoPositionState.editor = editor;
          this.logoPositionState.input = input;
          this.mountLogoPositionStage();
          if (this.logoPositionState.restoresSavedCrop) {
            this.restoreLogoCropState(this.logoPositionState.crop);
            this.logoPositionState.cropReady = this.isLogoCropReady(this.logoPositionState);
            this.repairLogoPositionStagePreview();
          }
          return;
        }
        if (this.logoPositionState) this.closeLogoPositionEditor(true);

        const currentCrop = this.captureLogoCropState(editor, input);
        const savedCrop = this.logoCropStates?.get(frame);
        const restoresSavedCrop = savedCrop && savedCrop.sourceFile === input.files?.[0];
        this.logoPositionState = {
          editor,
          input,
          frame,
          view: input.name?.includes(this.backLabel) ? 'back' : 'front',
          values,
          originalX: Number(values[2]),
          originalY: Number(values[3]),
          originalWidth: Number(values[4]),
          originalHeight: Number(values[14]),
          originalStage: this.host?.parentElement,
          crop: restoresSavedCrop ? { ...currentCrop, ...savedCrop } : currentCrop,
          restoresSavedCrop,
          cropReady: false,
          ready: false,
        };
        this.mountLogoPositionStage();
        if (restoresSavedCrop) {
          this.restoreLogoCropState(this.logoPositionState.crop);
          this.logoPositionState.cropReady = this.isLogoCropReady(this.logoPositionState);
          this.repairLogoPositionStagePreview();
        }
      }

      connectLogoPositionEditor() {
        if (!this.personalizer) this.personalizer = document.querySelector('.product-personalizer');
        if (!this.personalizer) return;

        const inlineEditor = [...document.querySelectorAll('.pplr-crop-active .crop_header[data-frame]')].find(
          (element) => element.getClientRects?.().length || element.offsetParent
        );
        const popupEditor = [...document.querySelectorAll('.pplr_crop-modal[data-main]')].find(
          (element) => element.getClientRects?.().length || element.offsetParent
        );
        const editor = inlineEditor || popupEditor;

        if (!editor) return;

        const frame =
          editor.dataset.frame ||
          editor.dataset.main ||
          editor.querySelector('.pplr-modal-img[data-frame]')?.dataset.frame;
        const input = [...this.personalizer.querySelectorAll('input[type="file"][data-frame]')].find(
          (field) => field.dataset.frame === frame && field.name?.includes('העלאת לוגו')
        );
        if (!input) return;

        this.openLogoPositionEditor(editor, input);
      }

      connectCoverCropEditor() {
        if (!this.personalizer) this.personalizer = document.querySelector('.product-personalizer');
        if (!this.personalizer) return;

        const inlineEditor = [...document.querySelectorAll('.pplr-crop-active .crop_header[data-frame]')].find(
          (element) => element.getClientRects?.().length || element.offsetParent
        );
        const popupEditor = [...document.querySelectorAll('.pplr_crop-modal[data-main]')].find(
          (element) => element.getClientRects?.().length || element.offsetParent
        );
        const editor = inlineEditor || popupEditor;
        if (!editor) return;

        const frame =
          editor.dataset.frame ||
          editor.dataset.main ||
          editor.querySelector('.pplr-modal-img[data-frame]')?.dataset.frame;
        const input = [...this.personalizer.querySelectorAll('input[type="file"][data-frame]')].find(
          (field) =>
            field.dataset.frame === frame &&
            (field.name?.includes('כריכה ראשית – העלאת עיצוב מלא') ||
              field.name?.includes('כריכה אחורית – העלאת עיצוב מלא'))
        );
        if (!input) return;

        this.openCoverCropEditor(editor, input);
      }

      openCoverCropEditor(editor, input) {
        const frame = input?.dataset.frame;
        if (!editor || !frame || this.coverCropState?.editor === editor) return;
        if (this.coverCropState) this.closeCoverCropEditor(false);

        const currentCrop = this.captureLogoCropState(editor, input);
        const savedCrop = this.coverCropStates?.get(frame);
        const restoresSavedCrop = savedCrop && savedCrop.sourceFile === input.files?.[0];
        const modalImage = editor.querySelector?.(`.pplr-modal-img[data-frame="${frame}"]`);
        const confirm = editor.querySelector?.('.p_r_c[onclick*="pplr_hide"]');

        this.coverCropState = {
          editor,
          input,
          frame,
          crop: restoresSavedCrop ? { ...currentCrop, ...savedCrop } : currentCrop,
        };
        this.coverCropReadyHandler = () => this.restoreSavedCoverCropState();
        this.coverCropConfirmHandler = () => this.closeCoverCropEditor(true);
        this.coverCropModalImage = modalImage;
        this.coverCropConfirm = confirm;
        if (modalImage && window.jQuery) {
          window.jQuery(modalImage).on('ready.personalNotebookCover', this.coverCropReadyHandler);
        }
        confirm?.addEventListener('click', this.coverCropConfirmHandler, true);
        if (restoresSavedCrop) this.restoreLogoCropState(this.coverCropState.crop);
      }

      restoreSavedCoverCropState() {
        const state = this.coverCropState;
        const savedCrop = state && this.coverCropStates?.get(state.frame);
        if (!state || !savedCrop || savedCrop.sourceFile !== state.input.files?.[0]) return;

        const currentCrop = this.captureLogoCropState(state.editor, state.input);
        state.crop = { ...currentCrop, ...savedCrop };
        this.restoreLogoCropState(state.crop);
      }

      closeCoverCropEditor(save) {
        const state = this.coverCropState;
        if (!state) return;

        if (save) {
          const crop = this.captureLogoCropState(state.editor, state.input);
          if (crop.cropperData && crop.canvasData && crop.cropBoxData) {
            this.coverCropStates ||= new Map();
            this.coverCropStates.set(state.frame, {
              sourceFile: state.input.files?.[0],
              cropperData: crop.cropperData,
              canvasData: crop.canvasData,
              cropBoxData: crop.cropBoxData,
            });
          }
        }
        if (this.coverCropModalImage && window.jQuery) {
          window.jQuery(this.coverCropModalImage).off('ready.personalNotebookCover', this.coverCropReadyHandler);
        }
        this.coverCropConfirm?.removeEventListener('click', this.coverCropConfirmHandler, true);
        this.coverCropState = null;
        this.coverCropModalImage = null;
        this.coverCropReadyHandler = null;
        this.coverCropConfirm = null;
        this.coverCropConfirmHandler = null;
      }

      captureLogoCropState(editor, input) {
        const frame = input.dataset.frame;
        const cropInput = this.personalizer.querySelector(`.pplr-crop[data-frame="${frame}"]`);
        const cropImage = this.personalizer.querySelector(`.crop_img_url[data-frame="${frame}"]`);
        const modalImage = editor.querySelector(`.pplr-modal-img[data-frame="${frame}"]`);
        const cropper = window.jQuery?.(modalImage)?.data?.('cropper');

        return {
          frame,
          cropInput,
          cropValue: cropInput?.value,
          cropImage,
          cropImageSrc: cropImage?.getAttribute('src'),
          cropper,
          cropperData: cropper?.getData?.(),
          canvasData: cropper?.getCanvasData?.(),
          cropBoxData: cropper?.getCropBoxData?.(),
        };
      }

      restoreLogoCropState(crop) {
        if (!crop) return;

        if (crop.cropInput) crop.cropInput.value = crop.cropValue;
        if (crop.cropImage && crop.cropImageSrc) crop.cropImage.setAttribute('src', crop.cropImageSrc);
        if (window.pplr_values?.[crop.frame]) window.pplr_values[crop.frame][22] = crop.cropValue;
        crop.cropper?.setData?.(crop.cropperData);
        crop.cropper?.setCanvasData?.(crop.canvasData);
        crop.cropper?.setCropBoxData?.(crop.cropBoxData);
        window.jQuery?.(crop.cropInput)?.trigger?.('change');
      }

      restoreSavedLogoCropState() {
        const state = this.logoPositionState;
        const savedCrop = state && this.logoCropStates?.get(state.frame);
        if (!state || !savedCrop || savedCrop.sourceFile !== state.input.files?.[0]) return;

        const currentCrop = this.captureLogoCropState(state.editor, state.input);
        state.crop = { ...currentCrop, ...savedCrop };
        this.restoreLogoCropState(state.crop);
        this.syncLogoPositionPreview();
      }

      saveLogoCropState(state) {
        const crop = this.captureLogoCropState(state.editor, state.input);
        if (!crop.cropperData || !crop.canvasData || !crop.cropBoxData) return;

        this.logoCropStates ||= new Map();
        this.logoCropStates.set(state.frame, {
          sourceFile: state.input.files?.[0],
          cropperData: crop.cropperData,
          canvasData: crop.canvasData,
          cropBoxData: crop.cropBoxData,
        });
      }

      mountLogoPositionStage() {
        const state = this.logoPositionState;
        if (!state) return;

        this.hideLegacyLogoPreview(state.editor);
        const sourceStage = this.querySelector(`[data-editor-view="${state.view}"] [data-editor-stage]`);
        const paper = sourceStage?.querySelector('[data-notebook-paper]')?.cloneNode(false);
        const binding = sourceStage?.querySelector('[data-notebook-binding]')?.cloneNode(false);
        const block = document.createElement('div');
        const instruction = document.createElement('p');
        const sizeControls = document.createElement('div');
        const enlarge = document.createElement('button');
        const shrink = document.createElement('button');
        const stage = document.createElement('div');
        const cover = document.createElement('div');
        const preview = document.createElement('img');
        const actions = document.createElement('div');
        const cancel = document.createElement('button');
        const save = document.createElement('button');

        block.className = 'personal-notebook-logo-position';
        instruction.className = 'personal-notebook-logo-position-instruction';
        instruction.textContent = 'גררי את הלוגו למיקום הרצוי';
        sizeControls.className = 'personal-notebook-logo-position-size-controls';
        enlarge.type = 'button';
        enlarge.className = 'personal-notebook-logo-position-size-button';
        enlarge.textContent = 'הגדלת הלוגו';
        shrink.type = 'button';
        shrink.className = 'personal-notebook-logo-position-size-button';
        shrink.textContent = 'הקטנת הלוגו';
        enlarge.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.resizeLogo(1.1);
        });
        shrink.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.resizeLogo(0.9);
        });
        sizeControls.append(enlarge, shrink);
        stage.className = 'personal-notebook-logo-position-stage';
        stage.dataset.editorView = state.view;
        cover.className = 'personal-notebook-logo-position-cover';
        preview.className = 'personal-notebook-logo-position-preview';
        preview.alt = '';
        preview.draggable = false;
        actions.className = 'personal-notebook-logo-position-actions';
        cancel.type = 'button';
        cancel.className =
          'personal-notebook-logo-position-button personal-notebook-logo-position-button--cancel';
        cancel.textContent = 'ביטול';
        save.type = 'button';
        save.className =
          'personal-notebook-logo-position-button personal-notebook-logo-position-button--save';
        save.textContent = 'שמירה';
        cancel.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.finishLogoPositionEditor(false);
        });
        save.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.finishLogoPositionEditor(true);
        });
        if (paper) {
          paper.className = 'personal-notebook-logo-position-paper';
          cover.append(paper);
        }
        cover.append(preview);
        stage.append(cover);
        if (binding) {
          binding.className = 'personal-notebook-logo-position-binding';
          stage.append(binding);
        }
        actions.append(cancel, save);
        block.append(instruction, sizeControls, stage, actions);

        const cropArea =
          state.editor.querySelector('.pplr-crop-area') ||
          state.editor.querySelector('.pplr-modal-body') ||
          state.editor.querySelector('.pplr-modal-image') ||
          state.editor;
        const header = state.editor.querySelector('header');
        if (
          window.matchMedia?.('(max-width: 749px)').matches &&
          cropArea !== state.editor &&
          cropArea.parentElement
        ) {
          cropArea.before(block);
        } else {
          (header || cropArea).after(block);
        }

        this.logoPositionBlock = block;
        this.logoPositionStage = stage;
        this.logoPositionCover = cover;
        this.logoPositionPreview = preview;
        this.logoPositionSizeControls = sizeControls;
        this.logoPositionEnlarge = enlarge;
        this.logoPositionShrink = shrink;
        this.logoPositionInstruction = instruction;
        this.logoPositionPointerDown = (event) => this.beginLogoPositionDrag(event);
        this.logoPositionPointerMove = (event) => this.moveLogoPositionDrag(event);
        this.logoPositionPointerUp = (event) => this.endLogoPositionDrag(event);
        cover.addEventListener('pointerdown', this.logoPositionPointerDown, true);
        cover.addEventListener('pointermove', this.logoPositionPointerMove, true);
        cover.addEventListener('pointerup', this.logoPositionPointerUp, true);
        cover.addEventListener('pointercancel', this.logoPositionPointerUp, true);

        const cropImage = this.personalizer.querySelector(`.crop_img_url[data-frame="${state.frame}"]`);
        const modalImage = state.editor.querySelector(`.pplr-modal-img[data-frame="${state.frame}"]`);
        state.cropReady = this.isLogoCropReady(state) && !state.restoresSavedCrop;
        if (cropImage) {
          this.logoPositionPreviewObserver = new MutationObserver(() => this.syncLogoPositionPreview());
          this.logoPositionPreviewObserver.observe(cropImage, {
            attributes: true,
            attributeFilter: ['src'],
          });
        }
        if (modalImage && window.jQuery) {
          this.logoPositionCropHandler = () => this.syncLogoPositionPreview(true);
          this.logoPositionCropReadyHandler = () => {
            if (this.logoPositionState !== state) return;

            this.restoreSavedLogoCropState();
            state.cropReady = true;
            this.repairLogoPositionStagePreview();
          };
          window.jQuery(modalImage).on('crop.personalNotebookLogo', this.logoPositionCropHandler);
          window.jQuery(modalImage).on('ready.personalNotebookLogo', this.logoPositionCropReadyHandler);
          this.logoPositionModalImage = modalImage;
        }

        const confirm = state.editor.querySelector('.p_r_c[onclick*="pplr_hide"]');
        if (confirm) {
          this.logoPositionConfirm = confirm;
          this.logoPositionConfirmHandler = () => this.closeLogoPositionEditor(true);
          confirm.addEventListener('click', this.logoPositionConfirmHandler, true);
        }

        this.logoPositionCancel = cancel;
        this.logoPositionSave = save;
        this.watchLogoPositionModalVisibility(
          this.logoPositionModal?.parentElement || this.logoPositionModal
        );

        this.setLogoPositionEditorReady(false);
        this.repairLogoPositionStagePreview();
        this.syncLogoPositionPreview();
      }

      watchLogoPositionModalVisibility(owner) {
        this.unwatchLogoPositionModalVisibility();
        if (!owner) return;

        this.logoPositionModalVisibilityOwner = owner;
        this.logoPositionModalWasVisible = Boolean(owner.getClientRects?.().length);
        this.logoPositionModalVisibilityObserver = new MutationObserver(() => {
          const visible = Boolean(owner.getClientRects?.().length);
          if (visible) {
            this.logoPositionModalWasVisible = true;
            return;
          }
          if (this.logoPositionModalWasVisible && this.logoPositionState) {
            this.closeLogoPositionEditor(true);
          }
        });
        this.logoPositionModalVisibilityObserver.observe(owner, {
          attributes: true,
          attributeFilter: ['style', 'class', 'hidden'],
        });
      }

      unwatchLogoPositionModalVisibility() {
        this.logoPositionModalVisibilityObserver?.disconnect();
        this.logoPositionModalVisibilityObserver = null;
        this.logoPositionModalVisibilityOwner = null;
        this.logoPositionModalWasVisible = false;
      }

      finishLogoPositionEditor(save) {
        const state = this.logoPositionState;
        const frame = state?.frame;
        if (!frame || (save && !state.ready)) return;

        this.closeLogoPositionEditor(save);
        window.pplr_hide?.(frame, save);
      }

      hideLegacyLogoPreview(editor) {
        const modal = editor.matches?.('.pplr_crop-modal')
          ? editor.querySelector('.pplr-modal-box')
          : editor.closest?.('.pplr-modal-box');
        const preview = modal?.querySelector('.pplr-p-left');
        if (!preview) return;

        modal.classList.add('personal-notebook-logo-editor-open');
        this.logoPositionModal = modal;
        this.logoPositionLegacyPreview = preview;
        this.logoPositionLegacyPreviewDisplay = preview.style.getPropertyValue('display');
        this.logoPositionLegacyPreviewPriority = preview.style.getPropertyPriority?.('display') || '';
        preview.style.setProperty('display', 'none', 'important');
      }

      restoreLegacyLogoPreview() {
        const preview = this.logoPositionLegacyPreview;
        if (!preview) return;

        if (this.logoPositionLegacyPreviewDisplay) {
          preview.style.setProperty(
            'display',
            this.logoPositionLegacyPreviewDisplay,
            this.logoPositionLegacyPreviewPriority
          );
        } else if (preview.style.removeProperty) {
          preview.style.removeProperty('display');
        } else {
          preview.style.display = '';
        }
        this.logoPositionLegacyPreview = null;
        this.logoPositionLegacyPreviewDisplay = null;
        this.logoPositionLegacyPreviewPriority = null;
        this.logoPositionModal?.classList.remove('personal-notebook-logo-editor-open');
        this.logoPositionModal = null;
      }

      syncLogoPositionPreview(useCropper = false) {
        const state = this.logoPositionState;
        const preview = this.logoPositionPreview;
        if (!state || !preview) return;

        const cropImage = this.personalizer.querySelector(`.crop_img_url[data-frame="${state.frame}"]`);
        const modalImage = state.editor.querySelector(`.pplr-modal-img[data-frame="${state.frame}"]`);
        const cropper = window.jQuery?.(modalImage)?.data?.('cropper');
        const croppedCanvas = useCropper ? cropper?.getCroppedCanvas?.() : null;
        const src =
          croppedCanvas?.toDataURL?.('image/png') ||
          cropImage?.getAttribute('src') ||
          modalImage?.getAttribute('src');
        if (src) preview.src = src;
        preview.style.left = `${Number(state.values[2])}%`;
        preview.style.top = `${Number(state.values[3])}%`;
        preview.style.width = `${Number(state.values[4])}%`;
        preview.style.height = `${Number(state.values[14])}%`;
      }

      isLogoCropReady(state = this.logoPositionState) {
        if (!state) return false;

        const modalImage = state.editor?.querySelector?.(
          `.pplr-modal-img[data-frame="${state.frame}"]`
        );
        if (!modalImage || !globalThis.window?.jQuery) return true;

        return Boolean(window.jQuery(modalImage).data?.('cropper'));
      }

      setLogoPositionEditorReady(ready) {
        const state = this.logoPositionState;
        const isReady = Boolean(ready);
        if (state) state.ready = isReady;

        const block = this.logoPositionBlock;
        if (block && block.classList.contains('is-loading') === isReady) {
          block.classList.toggle('is-loading', !isReady);
        }
        if (block && block.classList.contains('is-ready') !== isReady) {
          block.classList.toggle('is-ready', isReady);
        }

        const ariaBusy = String(!isReady);
        if (block?.getAttribute('aria-busy') !== ariaBusy) {
          block?.setAttribute('aria-busy', ariaBusy);
        }

        const ariaDisabled = String(!isReady);
        if (this.logoPositionCover?.getAttribute('aria-disabled') !== ariaDisabled) {
          this.logoPositionCover?.setAttribute('aria-disabled', ariaDisabled);
        }

        if (this.logoPositionInstruction) {
          const instruction = isReady
            ? 'גררי את הלוגו למיקום הרצוי'
            : 'מכינה את תצוגת הלוגו…';
          if (this.logoPositionInstruction.textContent !== instruction) {
            this.logoPositionInstruction.textContent = instruction;
          }
        }
        [
          this.logoPositionEnlarge,
          this.logoPositionShrink,
          this.logoPositionSave,
          this.logoPositionConfirm,
        ].forEach((control) => {
          if (control && control.disabled !== !isReady) control.disabled = !isReady;
        });
      }

      repairLogoPositionStagePreview() {
        const state = this.logoPositionState;
        if (!state || !this.host || !this.preview || !this.logoPositionCover) return;

        // Read Cropper's current state on every reconciliation. Its `ready`
        // event can belong to an editor node that Zepto replaces while keeping
        // the old node connected, so an event-latched boolean can stay false.
        if (!state.cropReady && !state.restoresSavedCrop && state.editor) {
          state.cropReady = this.isLogoCropReady(state);
        }

        // Cropper must finish initializing against Zepto's native preview tree.
        // Adopting its rendered canvas earlier removes a node that Zepto still owns,
        // which re-enters this repair through the body MutationObserver.
        if (!state.cropReady) {
          this.setLogoPositionEditorReady(false);
          return;
        }

        const renderedPreviews = [
          ...this.host.querySelectorAll('.pplr_preview_wrapper'),
          state.originalStage?.querySelector?.('.pplr_preview_wrapper'),
          ...(typeof document === 'undefined'
            ? []
            : document.querySelectorAll('.pplr_preview_wrapper')),
        ].filter(Boolean);
        const renderedPreview = renderedPreviews.find(
          (candidate) =>
            candidate !== this.preview &&
            candidate.querySelector('#pplr_canvas')?.dataset.frame === state.frame
        );
        const renderedCanvas = renderedPreview?.querySelector('#pplr_canvas');
        const modalCanvas = this.preview.querySelector('#pplr_canvas');
        if (renderedCanvas && modalCanvas && renderedCanvas !== modalCanvas) {
          modalCanvas.replaceWith(renderedCanvas);
          renderedPreview.remove();
        }

        const frameReady =
          this.preview.querySelector('#pplr_canvas')?.dataset.frame === state.frame;
        if (frameReady && state.cropReady) {
          if (this.host.parentElement !== this.logoPositionCover) {
            this.logoPositionCover.append(this.host);
          }
          if (this.preview.parentElement !== this.host) {
            this.host.append(this.preview);
          }
        }

        const ownsReadyFrame =
          frameReady &&
          state.cropReady &&
          this.host.parentElement === this.logoPositionCover &&
          this.preview.parentElement === this.host;
        this.setLogoPositionEditorReady(ownsReadyFrame);
      }

      resizeLogo(requestedScale) {
        this.repairLogoPositionStagePreview();
        const state = this.logoPositionState;
        if (!state?.ready) return;

        const width = Number(state.values[4]);
        const height = Number(state.values[14]);
        const maxWidth = 2 * Math.min(Number(state.values[2]), 100 - Number(state.values[2]));
        const maxHeight = 2 * Math.min(Number(state.values[3]), 100 - Number(state.values[3]));
        const maxScale = Math.min(maxWidth / width, maxHeight / height);
        const minScale = Math.max(5 / width, 5 / height);
        const scale =
          requestedScale > 1
            ? Math.max(1, Math.min(requestedScale, maxScale))
            : Math.min(1, Math.max(requestedScale, minScale));

        state.values[4] = Number((width * scale).toFixed(2));
        state.values[14] = Number((height * scale).toFixed(2));
        this.syncLogoPositionPreview();
        this.renderTextPosition(state.frame, true);
        this.repairLogoPositionStagePreview();
      }

      unmountLogoPositionStage() {
        this.unwatchLogoPositionModalVisibility();
        this.restoreLegacyLogoPreview();
        this.logoPositionPreviewObserver?.disconnect();
        this.logoPositionPreviewObserver = null;
        this.logoPositionCover?.removeEventListener('pointerdown', this.logoPositionPointerDown, true);
        this.logoPositionCover?.removeEventListener('pointermove', this.logoPositionPointerMove, true);
        this.logoPositionCover?.removeEventListener('pointerup', this.logoPositionPointerUp, true);
        this.logoPositionCover?.removeEventListener('pointercancel', this.logoPositionPointerUp, true);
        if (this.logoPositionModalImage && window.jQuery) {
          window.jQuery(this.logoPositionModalImage).off('crop.personalNotebookLogo', this.logoPositionCropHandler);
          window.jQuery(this.logoPositionModalImage).off('ready.personalNotebookLogo', this.logoPositionCropReadyHandler);
        }
        this.logoPositionConfirm?.removeEventListener('click', this.logoPositionConfirmHandler, true);
        const originalStage = this.logoPositionState?.originalStage;
        if (originalStage && this.host && this.preview) {
          originalStage.append(this.host);
          this.host.append(this.preview);
        }
        this.logoPositionBlock?.remove();
        this.logoPositionCancel?.remove();
        this.logoPositionSave?.remove();
        this.logoPositionBlock = null;
        this.logoPositionStage = null;
        this.logoPositionCover = null;
        this.logoPositionPreview = null;
        this.logoPositionSizeControls = null;
        this.logoPositionEnlarge = null;
        this.logoPositionShrink = null;
        this.logoPositionInstruction = null;
        this.logoPositionModalImage = null;
        this.logoPositionCropHandler = null;
        this.logoPositionCropReadyHandler = null;
        this.logoPositionConfirm = null;
        this.logoPositionCancel = null;
        this.logoPositionSave = null;
        this.logoPositionDrag = null;
      }

      closeLogoPositionEditor(save) {
        const state = this.logoPositionState;
        if (!state) return;

        if (save) {
          this.saveLogoCropState(state);
        } else {
          state.values[2] = state.originalX;
          state.values[3] = state.originalY;
          state.values[4] = state.originalWidth;
          state.values[14] = state.originalHeight;
          this.restoreLogoCropState(state.crop);
          this.renderTextPosition(state.frame, true);
        }

        this.unmountLogoPositionStage();
        this.logoPositionState = null;
      }

      beginLogoPositionDrag(event) {
        this.repairLogoPositionStagePreview();
        const state = this.logoPositionState;
        if (!state?.ready || !this.logoPositionCover) return;

        const stageBounds = this.logoPositionCover.getBoundingClientRect();
        const logoBounds = {
          width: (stageBounds.width * Number(state.values[4])) / 100,
          height: (stageBounds.height * Number(state.values[14])) / 100,
        };

        event.preventDefault();
        event.stopPropagation?.();
        this.logoPositionDrag = {
          pointerId: event.pointerId,
          frame: state.frame,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: Number(state.values[2]),
          startY: Number(state.values[3]),
          stageBounds,
          logoBounds,
        };
        this.logoPositionCover.setPointerCapture?.(event.pointerId);
        this.logoPositionCover.classList.add('is-dragging');
        this.logoPositionPreview.classList.add('is-dragging');
      }

      moveLogoPositionDrag(event) {
        const drag = this.logoPositionDrag;
        const state = this.logoPositionState;
        if (!drag || !state || event.pointerId !== drag.pointerId) return;

        event.preventDefault();
        event.stopPropagation?.();
        const halfWidth = (drag.logoBounds.width / drag.stageBounds.width) * 50;
        const halfHeight = (drag.logoBounds.height / drag.stageBounds.height) * 50;
        const x = drag.startX + ((event.clientX - drag.startClientX) / drag.stageBounds.width) * 100;
        const y = drag.startY + ((event.clientY - drag.startClientY) / drag.stageBounds.height) * 100;
        state.values[2] = Number(Math.min(100 - halfWidth, Math.max(halfWidth, x)).toFixed(2));
        state.values[3] = Number(Math.min(100 - halfHeight, Math.max(halfHeight, y)).toFixed(2));
        this.logoPositionPreview.style.left = `${state.values[2]}%`;
        this.logoPositionPreview.style.top = `${state.values[3]}%`;
        this.renderTextPosition(drag.frame);
      }

      endLogoPositionDrag(event) {
        const drag = this.logoPositionDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;

        event.stopPropagation?.();
        this.logoPositionCover.releasePointerCapture?.(event.pointerId);
        this.logoPositionCover.classList.remove('is-dragging');
        this.logoPositionPreview.classList.remove('is-dragging');
        this.logoPositionDrag = null;
        this.renderTextPosition(drag.frame, true);
        this.repairLogoPositionStagePreview();
      }

      ensureTextPositionDialog() {
        if (this.textPositionDialog) return;

        const dialog = document.createElement('dialog');
        dialog.className = 'personal-notebook-text-position-dialog';

        const content = document.createElement('div');
        content.className = 'personal-notebook-text-position-dialog-content';

        const heading = document.createElement('h2');
        heading.className = 'personal-notebook-text-position-dialog-heading';
        heading.textContent = 'בחירת שם או הקדשה';

        const instruction = document.createElement('p');
        instruction.className = 'personal-notebook-text-position-dialog-instruction';
        instruction.textContent = 'גררי את הכיתוב למיקום הרצוי';

        const stage = document.createElement('div');
        stage.className =
          'personal-notebook-editor-stage personal-notebook-text-position-modal-stage is-editor-active';
        stage.dataset.editorStage = '';

        const controls = document.createElement('div');
        controls.className = 'personal-notebook-text-position-dialog-controls';

        const controlColumns = document.createElement('div');
        controlColumns.className = 'personal-notebook-text-position-dialog-control-columns';

        const actions = document.createElement('div');
        actions.className = 'personal-notebook-text-position-dialog-actions';

        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.className =
          'personal-notebook-text-position-dialog-button personal-notebook-text-position-dialog-button--cancel';
        cancelButton.textContent = 'ביטול';

        const saveButton = document.createElement('button');
        saveButton.type = 'button';
        saveButton.className =
          'personal-notebook-text-position-dialog-button personal-notebook-text-position-dialog-button--save';
        saveButton.textContent = 'שמירה';

        actions.append(cancelButton, saveButton);
        controls.append(controlColumns);
        content.append(heading, instruction, stage, controls, actions);
        dialog.append(content);
        this.append(dialog);

        cancelButton.addEventListener('click', () => this.closeTextPositionModal(false));
        saveButton.addEventListener('click', () => this.closeTextPositionModal(true));
        dialog.addEventListener('cancel', (event) => {
          event.preventDefault();
          this.closeTextPositionModal(false);
        });

        this.textPositionDialog = dialog;
        this.textPositionDialogHeading = heading;
        this.textPositionDialogInstruction = instruction;
        this.textPositionModalStage = stage;
        this.textPositionDialogControls = controls;
        this.textPositionDialogControlColumns = controlColumns;
        this.textPositionCancelButton = cancelButton;
        this.textPositionSaveButton = saveButton;
      }

      openTextPositionModal(control) {
        const frameValues = window.pplr_values?.[control.frame];
        if (!this.host || !this.preview || !frameValues) return;

        this.ensureTextPositionDialog();
        if (this.textPositionModalOpen) this.closeTextPositionModal(false);
        this.activateEditor(control.view);

        const originalStage = this.host.parentElement;
        const binding = originalStage.querySelector('[data-notebook-binding]')?.cloneNode(false);
        const coverMask = originalStage.style.getPropertyValue('--personal-notebook-cover-mask');

        this.textPositionControl = {
          ...control,
          originalStage,
          originalState: this.captureTextSlotState(control.slot || control),
        };
        this.textPositionInputHandler = () => this.renderActiveTextPosition();
        control.text?.addEventListener('input', this.textPositionInputHandler);
        this.textPositionModalOpen = true;
        this.textPositionDialog.dataset.editorView = control.view;
        this.textPositionModalStage.classList.add('is-editor-active');
        this.textPositionModalStage.style.setProperty('--personal-notebook-cover-mask', coverMask);
        this.textPositionModalStage.append(this.host);
        this.mountTextPositionControls(control);
        if (binding) {
          this.textPositionModalBinding = binding;
          this.textPositionModalStage.append(binding);
        }
        this.bindTextPositionPreview();
        this.preview.classList.add('is-positioning-text');
        this.lockTextPositionPage();
        this.textPositionDialog.showModal();
        this.renderActiveTextPosition();
      }

      renderActiveTextPosition() {
        const control = this.textPositionControl;
        if (!control?.frame) return false;

        this.renderTextPosition(control.frame, true);
        return this.repairTextPositionModalPreview();
      }

      closeTextPositionModal(save) {
        const control = this.textPositionControl;
        if (!control) return;

        if (!save) {
          this.restoreTextSlotState(control.slot || control, control.originalState);
          this.repairTextPositionModalPreview();
        } else if (!control.text.value.trim() && control.slot?.defaultState) {
          this.restoreTextSlotState(control.slot, control.slot.defaultState);
        }
        control.text?.removeEventListener('input', this.textPositionInputHandler);
        this.textPositionInputHandler = null;

        if (this.textPositionDrag) this.preview?.releasePointerCapture?.(this.textPositionDrag.pointerId);
        this.preview?.classList.remove('is-positioning-text', 'is-dragging-text');
        this.textPositionModalBinding?.remove();
        this.textPositionModalBinding = null;
        this.unmountTextPositionControls();
        this.textPositionModalOpen = false;
        control.originalStage.append(this.host);
        this.textPositionDialog.close();
        this.unlockTextPositionPage();
        this.textPositionModalStage.classList.remove('is-editor-active');
        this.textPositionControl = null;
        this.textPositionDrag = null;
        this.renderTextSlotList(control.view);
        this.syncFloatingPreview(control.view);
      }

      captureTextSlotState(slot) {
        const frameValues = window.pplr_values?.[slot.frame] || [];
        return {
          text: slot.text?.value || '',
          font: slot.font?.value || '',
          color: slot.color?.value || '',
          colorValue: slot.color?.dataset?.value || '',
          size: slot.size?.value || '',
          frameValues: [...frameValues],
        };
      }

      restoreTextSlotState(slot, state) {
        if (!state) return;

        if (slot.text) slot.text.value = state.text;
        if (slot.font) slot.font.value = state.font;
        if (slot.color) {
          slot.color.value = state.color;
          if (slot.color.dataset) slot.color.dataset.value = state.colorValue;
        }
        if (slot.size) slot.size.value = state.size;

        const frameValues = window.pplr_values?.[slot.frame];
        if (frameValues) {
          frameValues.length = state.frameValues.length;
          state.frameValues.forEach((value, index) => {
            frameValues[index] = value;
          });
        }

        const selectedFont = slot.fontGroup?.querySelector?.('.pplr-selecter-selected');
        if (selectedFont) selectedFont.textContent = state.font;
        slot.colorGroup?.querySelectorAll?.('.pplrColor')?.forEach((element) => {
          element.classList.toggle(
            'selected',
            element.dataset.name === state.color || element.dataset.color === state.colorValue
          );
        });
        window.LoadPplrWithFont?.(slot.frame, true, slot.text);
      }

      mountTextPositionControls(control) {
        const groups = [control.textGroup, control.sizeGroup, control.fontGroup, control.colorGroup].filter(
          Boolean
        );
        if (!groups.length) return;

        this.textPositionControlLocations = groups.map((group) => ({
          group,
          parent: group.parentElement,
          nextSibling: group.nextSibling,
        }));
        const sizeLabel = control.sizeGroup?.querySelector?.('label');
        const fontLabel = control.fontGroup?.querySelector?.('label');
        if (sizeLabel) sizeLabel.textContent = 'גודל';
        if (fontLabel) fontLabel.textContent = 'פונט';

        if (control.textGroup) this.textPositionDialogControls.prepend(control.textGroup);
        if (control.sizeGroup) this.textPositionDialogControlColumns.append(control.sizeGroup);
        if (control.fontGroup) this.textPositionDialogControlColumns.append(control.fontGroup);
        if (control.colorGroup) this.textPositionDialogControls.append(control.colorGroup);
      }

      unmountTextPositionControls() {
        this.textPositionControlLocations?.forEach(({ group, parent, nextSibling }) => {
          if (!parent) return;

          if (nextSibling?.parentElement === parent) parent.insertBefore(group, nextSibling);
          else parent.append(group);
        });
        this.textPositionControlLocations = null;
      }

      bindTextPositionPreview() {
        if (!this.preview || this.textPositionPreview === this.preview) return;

        this.unbindTextPositionPreview();
        this.textPositionPointerDown = (event) => this.beginTextPositionDrag(event);
        this.textPositionPointerMove = (event) => this.moveTextPositionDrag(event);
        this.textPositionPointerUp = (event) => this.endTextPositionDrag(event);
        this.textPositionGestureBlocker = (event) => {
          if (!this.textPositionModalOpen) return;

          event.preventDefault();
          event.stopImmediatePropagation();
        };
        this.preview.addEventListener('pointerdown', this.textPositionPointerDown);
        this.preview.addEventListener('pointermove', this.textPositionPointerMove);
        this.preview.addEventListener('pointerup', this.textPositionPointerUp);
        this.preview.addEventListener('pointercancel', this.textPositionPointerUp);
        this.preview.addEventListener('touchstart', this.textPositionGestureBlocker, {
          passive: false,
          capture: true,
        });
        this.preview.addEventListener('touchmove', this.textPositionGestureBlocker, {
          passive: false,
          capture: true,
        });
        this.preview.addEventListener('mousemove', this.textPositionGestureBlocker, true);
        this.textPositionPreview = this.preview;
      }

      unbindTextPositionPreview() {
        if (!this.textPositionPreview) return;

        this.textPositionPreview.removeEventListener('pointerdown', this.textPositionPointerDown);
        this.textPositionPreview.removeEventListener('pointermove', this.textPositionPointerMove);
        this.textPositionPreview.removeEventListener('pointerup', this.textPositionPointerUp);
        this.textPositionPreview.removeEventListener('pointercancel', this.textPositionPointerUp);
        this.textPositionPreview.removeEventListener('touchstart', this.textPositionGestureBlocker, true);
        this.textPositionPreview.removeEventListener('touchmove', this.textPositionGestureBlocker, true);
        this.textPositionPreview.removeEventListener('mousemove', this.textPositionGestureBlocker, true);
        this.textPositionPreview = null;
      }

      lockTextPositionPage() {
        const body = document.body;
        this.textPositionScrollY = window.scrollY;
        this.textPositionBodyStyles = {
          position: body.style.position,
          top: body.style.top,
          width: body.style.width,
          overflow: body.style.overflow,
        };
        body.style.position = 'fixed';
        body.style.top = `-${this.textPositionScrollY}px`;
        body.style.width = '100%';
        body.style.overflow = 'hidden';
        document.documentElement.classList.add('personal-notebook-text-position-open');
      }

      unlockTextPositionPage() {
        if (!this.textPositionBodyStyles) return;

        Object.assign(document.body.style, this.textPositionBodyStyles);
        document.documentElement.classList.remove('personal-notebook-text-position-open');
        window.scrollTo(0, this.textPositionScrollY);
        this.textPositionBodyStyles = null;
      }

      stopTextPositionEditing() {
        if (!this.textPositionControl) return;

        if (this.textPositionModalOpen) {
          this.closeTextPositionModal(false);
          return;
        }

        this.preview?.classList.remove('is-positioning-text', 'is-dragging-text');
        this.textPositionControl = null;
        this.textPositionDrag = null;
      }

      beginTextPositionDrag(event) {
        const control = this.textPositionControl;
        const canvas = this.preview?.querySelector('#pplr_canvas');
        const frameValues = window.pplr_values?.[control?.frame];
        if (!control || !canvas || event.target !== canvas || !frameValues) return;

        event.preventDefault();
        event.stopPropagation?.();
        const bounds = canvas.getBoundingClientRect();
        this.textPositionDrag = {
          pointerId: event.pointerId,
          frame: control.frame,
          startClientX: event.clientX,
          startClientY: event.clientY,
          startX: Number(frameValues[2]),
          startY: Number(frameValues[3]),
          bounds,
        };
        this.textPositionLastRender = 0;
        this.preview.setPointerCapture?.(event.pointerId);
        this.preview.classList.add('is-dragging-text');
      }

      moveTextPositionDrag(event) {
        const drag = this.textPositionDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;

        event.preventDefault();
        event.stopPropagation?.();
        const frameValues = window.pplr_values?.[drag.frame];
        if (!frameValues) return;

        const x = drag.startX + ((event.clientX - drag.startClientX) / drag.bounds.width) * 100;
        const y = drag.startY + ((event.clientY - drag.startClientY) / drag.bounds.height) * 100;
        const limits = this.getTextPositionLimits(this.textPositionControl, this.preview.querySelector('#pplr_canvas'), frameValues);
        frameValues[2] = Number(Math.min(limits.maxX, Math.max(limits.minX, x)).toFixed(2));
        frameValues[3] = Number(Math.min(limits.maxY, Math.max(limits.minY, y)).toFixed(2));
        this.renderTextPosition(drag.frame);
      }

      getTextPositionLimits(control, canvas, frameValues) {
        const context = canvas.getContext('2d');
        const fontFile = String(frameValues[0] || '').split('/').pop().replace(/\.[^/.]+$/, '');
        const fontFamily = fontFile.replace('_', '') || 'sans-serif';
        const text = control.text.value || control.text.placeholder || '';
        let fontSize = (Number(frameValues[1]) || 20) * 0.75 * (canvas.height / 500);

        context.save();
        context.font = `${fontSize}pt ${fontFamily}`;
        let metrics = context.measureText(text);
        const maxWidth = (Number(frameValues[4]) / 100) * canvas.width;
        if (metrics.width > maxWidth) {
          fontSize *= maxWidth / metrics.width;
          context.font = `${fontSize}pt ${fontFamily}`;
          metrics = context.measureText(text);
        }
        context.restore();

        const textWidth = Math.min(metrics.width, maxWidth);
        const textHeight =
          metrics.actualBoundingBoxAscent + metrics.actualBoundingBoxDescent || fontSize * (96 / 72);
        const minX = Math.min(50, (textWidth / 2 / canvas.width) * 100 + 2);
        const minY = Math.min(50, (textHeight / 2 / canvas.height) * 100 + 2);

        return {
          minX,
          maxX: 100 - minX,
          minY,
          maxY: 100 - minY,
        };
      }

      endTextPositionDrag(event) {
        const drag = this.textPositionDrag;
        if (!drag || event.pointerId !== drag.pointerId) return;

        this.preview.releasePointerCapture?.(event.pointerId);
        this.preview.classList.remove('is-dragging-text');
        this.textPositionDrag = null;
        this.renderTextPosition(drag.frame, true);
      }

      renderTextPosition(frame, force = false) {
        const now = Date.now();
        if (!force && now - this.textPositionLastRender < 350) return;

        this.textPositionLastRender = now;
        window.LoadPplrWithFont?.(frame);
      }

      activateSlide(slide) {
        if (!slide) return;

        const index = Number(slide.dataset.galleryIndex);
        this.currentIndex = index;
        this.slides.forEach((item) => item.classList.toggle('is-active', item === slide));
        this.dots.forEach((dot) => {
          const active = Number(dot.dataset.galleryDot) === index;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-selected', String(active));
        });

        if (slide.dataset.editorView) this.activateEditor(slide.dataset.editorView);
      }

      activateEditor(view) {
        if (!this.host || !this.preview?.querySelector('#pplr_canvas')) return;

        if (this.textPositionControl?.view !== view) this.stopTextPositionEditing();
        const stage = this.querySelector(`[data-editor-view="${view}"] [data-editor-stage]`);
        const label = view === 'front' ? this.frontLabel : this.backLabel;
        const control = this.findViewControl(view);
        if (!stage) return;

        this.activeEditorView = view;
        this.syncing = true;
        this.querySelectorAll('[data-editor-stage]').forEach((element) => {
          element.classList.remove('is-editor-active');
        });
        this.host.append(this.preview);
        stage.append(this.host);
        stage.classList.add('is-editor-active');
        if (control && !control.classList.contains('pplr_active') && control.getAttribute('aria-selected') !== 'true') {
          control.click();
        }
        this.syncing = false;
        this.syncFloatingPreview(view);
      }

      goToIndex(index) {
        const slide = this.slides[index];
        if (!slide) return;

        this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
    }
  );
}
